



































































import { useInAppBack } from '@app/shared/composables/useInAppBack';
import { useLogoDestination } from '@src/app/shared/composables/useLogoDestination';
import {
    computed,
    defineComponent,
    onBeforeUnmount,
    PropType,
    ref,
    watch,
} from '@vue/composition-api';
import MobileHeaderAvatar from './components/MobileHeaderAvatar.vue';
import { useMobileHeaderData } from './composables/useMobileHeaderData';
import {
    useVueRouter,
    useVuexStore,
} from '@superbet-group/web.lib.vue-utilities';
import { ActionItem } from '@superbet-group/web.types';
import { NavigationDataStore } from '@store/modules/navigation';
import { shellLocalConfig } from '@src/config/shell/localConfig';
import { appLocale } from '@src/config/locale';
import { getShellConfig } from '@src/config/shell/config';
import {
    SdsFlag,
    SdsGlobalHeaderMobile,
} from '@superbet-group/web.lib.ui-components';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import { RouteName } from '@src/app/router/constants/RouteName';
import LanguageSwitcher from '@src/app/localization/components/LanguageSwitcher.vue';
import { SiteLanguageConfig } from '@modules/app/siteLanguage/siteLanguageConfig';
import { useHeaderBackbuttonOverride } from './composables/useHeaderBackbuttonOverride';
import { AuthenticationStore } from '@store/modules/data/authentication/authenticationIndex';
import QuickDepositButton from '@walletFederation/src/modules/quickDeposit/QuickDepositButton.vue';
import { getCurrentBrand } from '@src/app/shared/brand/brand';
import { AccountFlagNames } from '@modules/userProfile/account/FeatureFlags';
import { useGetFeatureFlag } from '@src/app/shared/featureFlags/useGetFeatureFlag';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';

export default defineComponent({
    name: 'SdsMobileHeader',
    props: {
        titleOverride: {
            type: String,
            required: false,
            default: undefined,
        },
        isBeingLoaded: {
            type: Boolean,
            required: false,
            default: false,
        },
        backOverride: {
            type: Function as PropType<() => void>,
            required: false,
            default: undefined,
        },
        hasProfileIcon: {
            type: Boolean,
            required: false,
            default: true,
        },
    },
    emits: [
        'click:back',
    ],
    setup(props, { emit, slots }) {
        const { route, router } = useVueRouter();
        const { routerBack } = useInAppBack({ router });
        const logoDestination = useLogoDestination();

        const hasSlot = (name: string) => !!slots[name];

        const hasRightSidePrependSlot = computed(() => hasSlot('rightSide:prepend'));

        const {
            title,
            isHome,
            isTopLevelRoute,
            isContracted,
            headerActionItems,
        } = useMobileHeaderData();

        const displayedTitle = computed(() => props.titleOverride ?? title.value);

        const isHubPage = computed(() => getShellConfig().remoteConfig.areSitesSplitByLicence && route.value.name === RouteName.GrowthMarketing.LandingIntro);

        const isNotOnRegisterRoute = computed(() => route.value.name !== RouteName.Account.Register);

        const navigationModule = useVuexStore<NavigationDataStore>('navigation');
        const currentNavigationContext = navigationModule.getter('currentNavigationContext');
        const authModule = useVuexStore<AuthenticationStore>('data/authentication');
        const user = authModule.getter('user');

        function onRightSideIconClick(item: ActionItem) {
            item.onClick(route.value, router.value);
        }

        const selectedLocaleObject = SiteLanguageConfig.availableLocales.find((locale) => locale.value === appLocale.locale);
        const isLanguageSwitchIconEnabled = ref(false);
        let featureSubscriptionUnsubscribeFn: () => void;

        watch(props, (nextProps) => {
            if (nextProps.isBeingLoaded) {
                return;
            }

            featureSubscriptionUnsubscribeFn = featureFlagService.subscribeToFeatureChanges('language-switcher-in-account-menu', (value) => {
                isLanguageSwitchIconEnabled.value = value && SiteLanguageConfig.hasLanguageSwitcher;
            }, false);
        }, {
            immediate: true,
        });

        onBeforeUnmount(() => {
            featureSubscriptionUnsubscribeFn?.();
        });

        const showLanguageSwitchIcon = computed(() => {
            return isLanguageSwitchIconEnabled.value && route.value.name === RouteName.Account.MyAccount;
        });

        const shouldShowQuickDepositButton = computed(() => {
            return user.value && !isContracted.value;
        });

        function onLanguageSwitchIconClick() {
            router.value.push({
                name: RouteName.Account.SwitchLanguage,
            });
        }

        watch(currentNavigationContext, (newValue) => {
            if (newValue) {
                const nextBrand = getCurrentBrand(newValue);
                document.querySelector('html')?.setAttribute('data-brand', nextBrand);
            }
        }, { immediate: true });

        const { maybeRunBackButtonOverride } = useHeaderBackbuttonOverride();
        function goBack() {
            if (props.backOverride) {
                props.backOverride();
                return;
            }
            const shouldStopDefaultBehaviour = maybeRunBackButtonOverride();
            if (shouldStopDefaultBehaviour) {
                return;
            }

            routerBack();

            emit('click:back', route.value);
        }

        const hasBottomSlot = computed(() => !!slots.bottom);
        const hasMiddleSlot = computed(() => !!slots.middle);

        const [
            showBalanceOnAllPages,
            onShowBalanceOnAllPagesReady,
        ] = useGetFeatureFlag<boolean | undefined>(
            AccountFlagNames.showBalanceInAllPages,
            undefined,
        );
        onShowBalanceOnAllPagesReady(sendBalanceShownOnHeaderEvent);
        watch(route, sendBalanceShownOnHeaderEvent);

        function sendBalanceShownOnHeaderEvent() {
            if (user.value) {
                const balanceWouldBeShownAnyway = isTopLevelRoute.value && !isHubPage.value;
                const balanceIsShown = showBalanceOnAllPages.value || balanceWouldBeShownAnyway;
                const couldNotDetermineExperimentParticipation = showBalanceOnAllPages.value === undefined;

                let group;
                if (couldNotDetermineExperimentParticipation || balanceWouldBeShownAnyway) {
                    // the balance would be shown anyway, so this is not part of the experiment.
                    group = 'neither';
                } else if (balanceIsShown) {
                    // the balance is shown when it would not normally be, so this is the variant group.
                    group = 'variant';
                } else {
                    // the balance is not shown as it normally would nopt, so this is the control group.
                    group = 'control';
                }
                if (group !== 'neither') {
                    AnalyticsSDK.getInstance().log('Balance_Shown_On_Header', {
                        route: route.value.path,
                        group,
                    });
                }
            }
        }

        return {
            currentNavigationContext,
            brand: shellLocalConfig.logo,
            logoDestination,
            showLanguageSwitchIcon,
            shouldShowQuickDepositButton,
            currentLocaleFlag: selectedLocaleObject?.flag,
            displayedTitle,
            isHome,
            isTopLevelRoute,
            isContracted,
            headerActionItems,
            isHubPage,
            isNotOnRegisterRoute,
            hasBottomSlot,
            hasMiddleSlot,
            hasRightSidePrependSlot,
            showBalanceOnAllPages,
            onRightSideIconClick,
            onLanguageSwitchIconClick,
            goBack,
        };
    },
    components: {
        MobileHeaderAvatar,
        QuickDepositButton,
        SdsFlag,
        LanguageSwitcher,
        SdsGlobalHeaderMobile,
    },
});
